import { v4 as uuidv4 } from "uuid";
import { baseFormDefaults } from "../baseFormDefaults";
import { recurrenceToForm } from "../recurrenceToForm";
import { penceToPounds, poundsToPence } from "../../../../utils";
import { FormData2 } from "../GenericTable";
import {
  every_x_days,
  every_x_months,
  every_x_months_last_day,
  every_x_months_last_working_day,
  every_x_weeks,
  every_x_years,
  IncomesItem,
  OutgoingItem,
  RecurrenceRuleSchema,
  Schedule,
  weekdays,
} from "../../../../backend/types-v2";
import { ChronoUnit, DayOfWeek, LocalDate } from "@js-joda/core";
import { jsDateToLocalDate } from "./utils";
import { localDateToJSDate } from "../../../../utils";

const CHRONO_UNIT_MAP: Record<string, ChronoUnit> = {
  DAYS: ChronoUnit.DAYS,
  WEEKS: ChronoUnit.WEEKS,
  MONTHS: ChronoUnit.MONTHS,
  YEARS: ChronoUnit.YEARS,
};

const chronoUnitToString = (unit: ChronoUnit): string => {
  if (unit === ChronoUnit.DAYS) return "DAYS";
  if (unit === ChronoUnit.WEEKS) return "WEEKS";
  if (unit === ChronoUnit.MONTHS) return "MONTHS";
  if (unit === ChronoUnit.YEARS) return "YEARS";
  throw new Error(`Unknown ChronoUnit: ${unit}`);
};

export type CashflowCapabilities = {
  taxable: boolean;
  parentalLeave: boolean;
  redundancy: boolean;
  bonuses: boolean;
};

export type CashflowAdapter<TItem> = {
  capabilities: CashflowCapabilities;
  toForm(item: TItem): FormData2;
  fromForm(form: FormData2, id?: string): TItem;
};

const nextDateForWeekday = (weekday: DayOfWeek): Date => {
  const today = LocalDate.now();
  const diff = (weekday.value() - today.dayOfWeek().value() + 7) % 7;
  const target = diff === 0 ? today : today.plusDays(diff);
  return localDateToJSDate(target);
};

const formToIncomesItem = (form: FormData2, id = uuidv4()): IncomesItem => {
  const redundancy =
    form.redundancy && form.redundancy.endDate
      ? {
          endDate: jsDateToLocalDate(form.redundancy.endDate),
          paidOn: form.redundancy.paidOn
            ? jsDateToLocalDate(form.redundancy.paidOn)
            : jsDateToLocalDate(form.redundancy.endDate),
          weeks: form.redundancy.weeks ?? 0,
          payBasis: form.redundancy.payBasis,
          noticeMonths:
            form.redundancy.hasNotice && form.redundancy.noticeMonths
              ? form.redundancy.noticeMonths
              : undefined,
          holidayDays: form.redundancy.holidayDays
            ? form.redundancy.holidayDays
            : undefined,
        }
      : undefined;

  const dateFilters = {
    dateFromInclusive: form.startsFrom
      ? jsDateToLocalDate(form.startsFrom)
      : undefined,
    // Redundancy end date is authoritative for when the salary stops.
    dateToInclusive: redundancy
      ? redundancy.endDate
      : form.endsOn
        ? jsDateToLocalDate(form.endsOn)
        : undefined,
  };

  const bonuses = form.bonuses
    ? form.bonuses
        .filter((b) => b.date)
        .map((b) =>
          b.kind === "fixed"
            ? {
                date: jsDateToLocalDate(b.date!),
                kind: "fixed" as const,
                amountPence: poundsToPence(b.amount ?? 0),
              }
            : {
                date: jsDateToLocalDate(b.date!),
                kind: "percentage" as const,
                percentage: (b.percentage ?? 0) / 100,
              },
        )
    : undefined;

  return {
    id,
    type: "INCOME_ITEM",
    description: form.description,
    amountPence: poundsToPence(form.amount!),
    taxable: form.isTaxable ?? true,
    userId: form.taxProfileId!,
    recurrence: formToRecurrence(form),
    dateFilters,
    redundancy,
    bonuses,
    parentalLeavePolicy: form.parentalLeavePolicy
      ? {
          startsFrom: form.parentalLeavePolicy.startsFrom
            ? jsDateToLocalDate(form.parentalLeavePolicy.startsFrom)
            : LocalDate.now(),
          afterPolicy: form.parentalLeavePolicy.afterPolicy,
          sequentialRules: form.parentalLeavePolicy.sequentialRules.map((r) =>
            r.amountType === "smp"
              ? {
                  amountType: "smp" as const,
                  chronoUnits: CHRONO_UNIT_MAP[r.chronoUnits],
                  amountOfChronoUnits: r.amountOfChronoUnits,
                }
              : {
                  amountType: "percentage" as const,
                  percentage: r.percentage / 100,
                  chronoUnits: CHRONO_UNIT_MAP[r.chronoUnits],
                  amountOfChronoUnits: r.amountOfChronoUnits,
                },
          ),
        }
      : undefined,
  };
};

const formToOutgoingsItem = (form: FormData2, id = uuidv4()): OutgoingItem => {
  const dateFilters =
    form.startsFrom || form.endsOn
      ? {
          dateFromInclusive: form.startsFrom
            ? jsDateToLocalDate(form.startsFrom)
            : undefined,
          dateToInclusive: form.endsOn
            ? jsDateToLocalDate(form.endsOn)
            : undefined,
        }
      : undefined;

  return {
    id,
    type: "OUTGOINGS_ITEM",
    description: form.description,
    amountPence: poundsToPence(form.amount!),
    recurrence: formToRecurrence(form),
    dateFilters,
  };
};

export const formToRecurrence = (form: FormData2): Schedule => {
  const {
    scheduleType,
    frequency,
    interval,
    monthlyRule,
    endsOn,
    oneOffDates,
    yearlyRule,
  } = form;

  const anchorDate = form.startsFrom
    ? jsDateToLocalDate(form.startsFrom)
    : LocalDate.EPOCH_0;

  if (scheduleType === "one-off") {
    if (!oneOffDates || oneOffDates.length === 0) {
      throw new Error("One-off schedule requires dates");
    }

    const nonNullScheduledDates: LocalDate[] = form.oneOffDates
      ? form.oneOffDates
          .filter((d): d is Date => d != null)
          .map((x) => jsDateToLocalDate(x))
      : [];

    return RecurrenceRuleSchema.parse({
      kind: "specific-dates",
      dates: nonNullScheduledDates,
    });
  }

  if (!frequency || !interval) {
    throw new Error("Repeating schedule requires frequency and interval");
  }

  switch (frequency) {
    case "monthly": {
      if (!monthlyRule) {
        throw new Error("Monthly frequency requires monthlyRule");
      }

      if (monthlyRule.type === "last-working-day") {
        return RecurrenceRuleSchema.parse({
          kind: every_x_months_last_working_day,
          interval,
          align: "last-working-day", // remove this redudnant field??
          recurrenceAnchorDate: anchorDate,
        });
      }

      if (monthlyRule.type === "last-day") {
        return RecurrenceRuleSchema.parse({
          kind: every_x_months_last_day,
          align: "last-day", // remove this redudnant field??
          interval,
          recurrenceAnchorDate: anchorDate,
        });
      }

      if (monthlyRule.type === "fixed-day") {
        return RecurrenceRuleSchema.parse({
          kind: every_x_months,
          align: "fixed-day", // remove this redudnant field??
          interval,
          dayOfMonth: Number(monthlyRule.day),
          recurrenceAnchorDate: anchorDate,
        });
      }

      throw new Error(`Unsupported monthlyRule ${monthlyRule.type}`);
    }

    case "yearly": {
      if (!yearlyRule) {
        throw new Error("");
      }
      return RecurrenceRuleSchema.parse({
        kind: every_x_years,
        interval,
        month: yearlyRule.month.value(),
        dayOfMonth: yearlyRule.day,
        recurrenceAnchorDate: anchorDate,
      });
    }

    case "weekly": {
      const weekday =
        form.weekday && form.weekday.length > 0
          ? DayOfWeek.valueOf(form.weekday)
          : anchorDate.dayOfWeek();
      return RecurrenceRuleSchema.parse({
        kind: every_x_weeks,
        interval,
        weekday,
      });
    }

    case "daily":
      return RecurrenceRuleSchema.parse({
        kind: every_x_days,
        interval,
        recurrenceAnchorDate: anchorDate,
        endDate: endsOn ? jsDateToLocalDate(endsOn) : null,
      });

    case "weekdays":
      return RecurrenceRuleSchema.parse({
        kind: weekdays,
        recurrenceAnchorDate: anchorDate,
        endDate: endsOn ? jsDateToLocalDate(endsOn) : null,
      });

    default:
      throw new Error(`Unsupported frequency ${frequency}`);
  }
};

export const incomesFormAdapter: CashflowAdapter<IncomesItem> = {
  toForm(x: IncomesItem): FormData2 {
    const base = recurrenceToForm(x.recurrence);

    const startsFrom = x.dateFilters?.dateFromInclusive
      ? localDateToJSDate(x.dateFilters.dateFromInclusive)
      : x.recurrence.kind === every_x_weeks
        ? nextDateForWeekday(x.recurrence.weekday)
        : null;

    return {
      ...baseFormDefaults,
      description: x.description,
      amount: penceToPounds(x.amountPence),
      taxProfileId: x.userId,
      isTaxable: x.taxable,
      ...base,
      startsFrom,
      endsOn: x.dateFilters?.dateToInclusive
        ? localDateToJSDate(x.dateFilters.dateToInclusive)
        : null,
      redundancy: x.redundancy
        ? {
            endDate: localDateToJSDate(x.redundancy.endDate),
            paidOn: localDateToJSDate(
              x.redundancy.paidOn ?? x.redundancy.endDate,
            ),
            weeks: x.redundancy.weeks,
            payBasis: x.redundancy.payBasis,
            hasNotice: !!x.redundancy.noticeMonths,
            noticeMonths: x.redundancy.noticeMonths ?? null,
            holidayDays: x.redundancy.holidayDays ?? null,
          }
        : null,
      bonuses: x.bonuses
        ? x.bonuses.map((b) => ({
            date: localDateToJSDate(b.date),
            kind: b.kind ?? "percentage",
            percentage:
              b.percentage != null ? Math.round(b.percentage * 100) : null,
            amount: b.amountPence != null ? penceToPounds(b.amountPence) : null,
          }))
        : null,
      parentalLeavePolicy: x.parentalLeavePolicy
        ? {
            startsFrom: localDateToJSDate(x.parentalLeavePolicy.startsFrom),
            afterPolicy: x.parentalLeavePolicy.afterPolicy,
            sequentialRules: x.parentalLeavePolicy.sequentialRules.map((r) =>
              r.amountType === "smp"
                ? {
                    amountType: "smp" as const,
                    percentage: 100,
                    chronoUnits: chronoUnitToString(r.chronoUnits),
                    amountOfChronoUnits: r.amountOfChronoUnits,
                  }
                : {
                    amountType: "percentage" as const,
                    percentage: r.percentage * 100,
                    chronoUnits: chronoUnitToString(r.chronoUnits),
                    amountOfChronoUnits: r.amountOfChronoUnits,
                  },
            ),
          }
        : null,
    };
  },

  fromForm(x: FormData2, id?: string): IncomesItem {
    return formToIncomesItem(x, id);
  },

  capabilities: {
    taxable: true,
    parentalLeave: true,
    redundancy: true,
    bonuses: true,
  },
};

export const outgoingsFormAdapter: CashflowAdapter<OutgoingItem> = {
  toForm(x: OutgoingItem): FormData2 {
    const base = recurrenceToForm(x.recurrence);

    const startsFrom = x.dateFilters?.dateFromInclusive
      ? localDateToJSDate(x.dateFilters.dateFromInclusive)
      : x.recurrence.kind === every_x_weeks
        ? nextDateForWeekday(x.recurrence.weekday)
        : null;

    return {
      ...baseFormDefaults,
      description: x.description,
      amount: penceToPounds(x.amountPence),
      taxProfileId: null,
      isTaxable: false,
      ...base,
      startsFrom,
      endsOn: x.dateFilters?.dateToInclusive
        ? localDateToJSDate(x.dateFilters.dateToInclusive)
        : null,
    };
  },

  fromForm(x: FormData2, id?: string): OutgoingItem {
    return formToOutgoingsItem(x, id);
  },

  capabilities: {
    taxable: false,
    parentalLeave: false,
    redundancy: false,
    bonuses: false,
  },
};
